import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Pin, X, Check } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

// Soft tint + accessible dark text derived from the pro's saved pin color.
function pinTint(hex) {
  const m = /^#?([0-9a-fA-F]{6})$/.exec((hex || "").trim());
  if (!m) return { bg: "#E1EDE7", fg: "#245544" };
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  const bg = `rgb(${Math.round(r * 0.18 + 209.1)},${Math.round(g * 0.18 + 209.1)},${Math.round(b * 0.18 + 209.1)})`;
  let f = 0.5;
  if ((0.2126 * r + 0.7152 * g + 0.0722 * b) * f / 255 > 0.42) f = 0.4;
  return { bg, fg: `rgb(${Math.round(r * f)},${Math.round(g * f)},${Math.round(b * f)})` };
}

/**
 * Compact pin trigger used on Search results and Pro profile.
 * - For visitors / pros / admins → routes to login or stays passive.
 * - For clients → pops a section picker (suggested = pro.category).
 * - Toggles to "Pinned" state after successful pin.
 * - Surfaces the 20-pin cap with a CTA that opens /board.
 */
export default function PinButton({ pro, size = "sm", variant = "chip", className = "", label, pinnedLabel = "Pinned", onChange }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [section, setSection] = useState(pro?.category || "Trusted Pros");
  const [knownSections, setKnownSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const popRef = useRef(null);

  // Hydrate "already pinned" state for clients
  useEffect(() => {
    if (user?.role !== "client" || !pro?.user_id) return;
    api.get("/boards/me").then(r => {
      const all = r.data.pins || [];
      setPinned(all.some(p => p.user_id === pro.user_id));
      setKnownSections([...new Set(all.map(p => p.section))]);
    }).catch(() => {});
  }, [user?.id, pro?.user_id]);

  // Close popover on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (popRef.current && !popRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const handleClick = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!user) { toast("Sign in to pin pros to your board"); navigate("/login"); return; }
    if (user.role !== "client") { toast("Only clients can pin pros"); return; }
    if (pinned) {
      unpin();
      return;
    }
    setSection(pro?.category || "Trusted Pros");
    setOpen(true);
  };

  const submitPin = async (e) => {
    e?.preventDefault?.(); e?.stopPropagation?.();
    setLoading(true);
    try {
      const r = await api.post(`/boards/pin/${pro.user_id}`, { section });
      setPinned(true); setOpen(false);
      toast.success(`Pinned to "${r.data.section}"`);
      onChange?.();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d?.code === "PIN_LIMIT_REACHED") {
        toast.error(d.message, {
          action: { label: "Open My Board", onClick: () => navigate("/board") },
          duration: 6000,
        });
        setOpen(false);
      } else {
        toast.error(typeof d === "string" ? d : "Could not pin");
      }
    } finally { setLoading(false); }
  };

  const unpin = async (e) => {
    e?.preventDefault?.(); e?.stopPropagation?.();
    try {
      await api.delete(`/boards/pin/${pro.user_id}`);
      setPinned(false);
      toast.success("Removed from board");
      onChange?.();
    } catch { toast.error("Could not remove"); }
  };

  const sizeCls = size === "lg"
    ? "px-4 py-2 text-sm"
    : variant === "icon"
      ? "p-1.5"
      : "px-2.5 py-1 text-xs";

  const tint = pinned ? pinTint(pro?.pin_color) : null;

  return (
    <div className={`relative inline-block ${className}`} ref={popRef} onClick={e => e.stopPropagation()}>
      <button
        type="button"
        onClick={handleClick}
        data-testid={`pin-btn-${pro?.user_id}`}
        title={pinned ? "Remove from Board" : "Pin to My Board"}
        style={pinned ? { background: tint.bg, color: tint.fg } : undefined}
        className={`inline-flex items-center gap-1 rounded-full font-semibold border transition shadow-sm
          ${pinned
            ? "border-transparent"
            : "bg-white text-[#222] border-[#D3DAD5] hover:bg-[#FAFBFA]"}
          ${sizeCls}`}
      >
        <Pin size={size === "lg" ? 14 : 12} className={pinned ? "fill-current" : ""}/>
        {variant !== "icon" && (
          <span>{pinned ? pinnedLabel : (label || "Pin")}</span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 z-50 bg-white border border-black/10 shadow-xl rounded-2xl p-4 w-72"
          data-testid={`pin-popover-${pro?.user_id}`}
          onClick={e => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-bold uppercase tracking-wider text-[#222]/60">Add to a section</p>
            <button onClick={() => setOpen(false)} className="p-0.5 rounded-full hover:bg-black/5" data-testid="pin-popover-close">
              <X size={14}/>
            </button>
          </div>
          <input
            value={section}
            onChange={e => setSection(e.target.value.slice(0, 40))}
            placeholder="e.g. Home Repairs"
            className="w-full px-3 py-2 text-sm border border-black/10 rounded-lg outline-none focus:ring-2 focus:ring-[#2E6B57]"
            data-testid="pin-section-input"
            autoFocus
            onClick={e => e.stopPropagation()}
          />
          {knownSections.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {knownSections.slice(0, 6).map(s => (
                <button
                  key={s}
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setSection(s); }}
                  className={`text-[10px] px-2 py-0.5 rounded-full border ${section === s ? "bg-[#2E6B57] text-[#FFFFFF] border-transparent" : "border-black/10 hover:bg-black/5"}`}
                  data-testid={`pin-section-chip-${s.replace(/\s+/g,'-')}`}
                >{s}</button>
              ))}
            </div>
          )}
          <button
            onClick={submitPin}
            disabled={loading || !section.trim()}
            className="mt-3 w-full btn-action py-2 text-sm disabled:opacity-50 flex items-center justify-center gap-1"
            data-testid="pin-confirm-btn"
          >
            <Check size={14}/> Pin to My Board
          </button>
        </div>
      )}
    </div>
  );
}
